"use client";

import { useState } from "react";

const sections = [
  {
    id: "using-pasoma",
    title: "Using Pasoma",
    content: [
      "Pasoma is an AI assistant for sending and receiving money via WhatsApp. You can use it only for lawful personal or business transactions.",
      "You must be at least 18 years old to use Pasoma.",
      "You're responsible for keeping your account secure. Don't share your PIN or any access credentials with anyone.",
      "If you think your account has been compromised, contact us at hello@pasoma.ai right away.",
    ],
  },
  {
    id: "what-we-provide",
    title: "What we provide",
    content: [
      "Pasoma helps you initiate and track money transfers through WhatsApp. We're a tool — we don't hold your funds or act as a bank.",
      "The actual movement of money is handled by licensed payment partners. Their terms also apply when you make a transaction.",
      "We aim to keep the service available at all times, but we can't guarantee it. Downtime can happen.",
    ],
  },
  {
    id: "your-responsibilities",
    title: "Your responsibilities",
    content: [
      "You agree not to use Pasoma for anything illegal — fraud, money laundering, sanctions evasion, or any activity that breaks local or international law.",
      "You'll provide accurate information when prompted, including identity and payment details.",
      "You won't try to reverse-engineer, scrape, or abuse the service.",
      "You're responsible for any transactions you initiate. Double-check recipient details before confirming.",
    ],
  },
  {
    id: "payments-and-fees",
    title: "Payments and fees",
    content: [
      "Pasoma may charge a small fee per transaction. This will always be shown to you before you confirm.",
      "Payment partners may have their own fees. These are separate from Pasoma's charges.",
      "We don't store your card or bank account numbers. Payment details are handled directly by our licensed payment partners.",
      "If a transaction fails, funds are typically returned within 3–5 business days depending on your bank.",
    ],
  },
  {
    id: "ai-limitations",
    title: "AI limitations",
    content: [
      "The Pasoma assistant is an AI. It won't always get things right.",
      "Don't rely on it for financial, legal, or tax advice. It's here to help you move money, not to advise you on it.",
      "If something goes wrong because of an AI error on our end, contact us. We'll look into it.",
      "We may update the AI's behavior at any time to improve accuracy or safety.",
    ],
  },
  {
    id: "termination",
    title: "Suspending or closing your account",
    content: [
      "You can stop using Pasoma and request account deletion at any time by emailing hello@pasoma.ai.",
      "We can suspend or close accounts that violate these terms, with or without notice depending on the severity.",
      "After account closure, we'll keep transaction records for up to 2 years as required by law. Other personal data will be deleted within 30 days.",
    ],
  },
  {
    id: "liability",
    title: "Our liability",
    content: [
      "We're not liable for losses that result from: unauthorized transactions you failed to report promptly, errors in information you provided, or downtime outside our control.",
      "Our total liability to you in any situation is capped at the amount of fees you paid to Pasoma in the 3 months before the incident.",
      "These terms don't affect any rights you have under applicable consumer protection law.",
    ],
  },
  {
    id: "changes",
    title: "Changes to these terms",
    content: [
      "We may update these terms from time to time. If we make a significant change, we'll let you know via WhatsApp or email before it takes effect.",
      "Continuing to use Pasoma after a change means you accept the updated terms.",
      "The latest version will always be at pasoma.ai/terms.",
    ],
  },
];

const glanceItems = [
  { label: "Who can use it", value: "Anyone 18+ with a WhatsApp account" },
  { label: "What it's for", value: "Sending and receiving money" },
  { label: "What's not allowed", value: "Illegal transactions of any kind" },
  { label: "Fees", value: "Shown before you confirm any transaction" },
  { label: "Close my account", value: "Email hello@pasoma.ai" },
  { label: "Governing law", value: "Federal Republic of Nigeria" },
];

export default function TermsPage() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <header style={styles.header}>
          <h1 style={styles.title}>Terms of Service</h1>
          <p style={styles.subtitle}>
            These terms cover what you can expect from Pasoma and what we expect
            from you. Last updated: February 2026.
          </p>
        </header>

        {/* At a Glance */}
        <section style={styles.glanceSection} aria-label="Terms at a glance">
          <h2 style={styles.sectionHeading}>Overview</h2>
          <div style={styles.glanceGrid}>
            {glanceItems.map((item) => (
              <div key={item.label} style={styles.glanceCard}>
                <span style={styles.glanceLabel}>{item.label}</span>
                <span style={styles.glanceValue}>{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Full Terms — Accordion */}
        <section aria-label="Full terms of service">
          <h2 style={styles.sectionHeading}>Full terms</h2>
          <div style={styles.accordionList}>
            {sections.map((section) => {
              const isOpen = openSection === section.id;
              return (
                <div key={section.id} style={styles.accordionItem}>
                  <button
                    id={`accordion-btn-${section.id}`}
                    aria-expanded={isOpen}
                    aria-controls={`accordion-content-${section.id}`}
                    onClick={() => toggle(section.id)}
                    style={styles.accordionButton}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "rgba(255,255,255,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        isOpen ? "rgba(255,255,255,0.04)" : "transparent";
                    }}
                  >
                    <span style={styles.accordionTitle}>{section.title}</span>
                    <span
                      style={{
                        ...styles.accordionIcon,
                        transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                      }}
                    >
                      +
                    </span>
                  </button>
                  <div
                    id={`accordion-content-${section.id}`}
                    role="region"
                    aria-labelledby={`accordion-btn-${section.id}`}
                    style={{
                      ...styles.accordionContent,
                      maxHeight: isOpen ? "600px" : "0px",
                      opacity: isOpen ? 1 : 0,
                    }}
                  >
                    <ul style={styles.contentList}>
                      {section.content.map((point, i) => (
                        <li key={i} style={styles.contentItem}>
                          <span style={styles.bullet}>—</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Agreement note */}
        <div style={styles.noticeBox}>
          <p style={styles.noticeText}>
            By sending a message to the Pasoma WhatsApp assistant, you agree to
            these terms. If you don&apos;t agree, please don&apos;t use the
            service.
          </p>
        </div>

        {/* Contact */}
        <footer style={styles.footer}>
          <h2 style={styles.sectionHeading}>Questions?</h2>
          <p style={styles.footerText}>
            Reach out at{" "}
            <a href="mailto:hello@pasoma.ai" style={styles.link}>
              hello@pasoma.ai
            </a>
            . We&apos;ll respond within 2 business days.
          </p>
          <div style={styles.footerLinks}>
            <a href="/privacy-policy" style={styles.subLink}>
              Privacy Policy
            </a>
          </div>
          <div style={styles.footerSeparator} />
          <p style={styles.copyright}>
            © {new Date().getFullYear()} Pasoma. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#0a0a0a",
    color: "#e5e5e5",
    fontFamily: "var(--font-dm), DM Sans, sans-serif",
    padding: "0 1rem",
  },
  container: {
    maxWidth: "720px",
    margin: "0 auto",
    padding: "4rem 0 6rem",
  },
  header: {
    marginBottom: "3.5rem",
  },
  title: {
    fontSize: "clamp(2rem, 5vw, 3rem)",
    fontWeight: 700,
    lineHeight: 1.15,
    color: "#ffffff",
    marginBottom: "1rem",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "1rem",
    color: "#888",
    lineHeight: 1.7,
    maxWidth: "560px",
  },
  glanceSection: {
    marginBottom: "3rem",
  },
  sectionHeading: {
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    color: "#666",
    marginBottom: "1.25rem",
  },
  glanceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "1px",
    background: "rgba(255,255,255,0.06)",
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.06)",
  },
  glanceCard: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.4rem",
    padding: "1.25rem",
    background: "#0f0f0f",
  },
  glanceLabel: {
    fontSize: "0.75rem",
    color: "#666",
    fontWeight: 500,
    letterSpacing: "0.04em",
  },
  glanceValue: {
    fontSize: "0.9rem",
    color: "#d4d4d4",
    fontWeight: 500,
    lineHeight: 1.4,
  },
  divider: {
    height: "1px",
    background: "rgba(255,255,255,0.06)",
    margin: "3rem 0",
  },
  accordionList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0",
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.06)",
  },
  accordionItem: {
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  accordionButton: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1.25rem 1.5rem",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#e5e5e5",
    textAlign: "left" as const,
    transition: "background 0.2s",
  },
  accordionTitle: {
    fontSize: "0.975rem",
    fontWeight: 500,
    color: "#e5e5e5",
  },
  accordionIcon: {
    fontSize: "1.35rem",
    color: "#666",
    fontWeight: 300,
    lineHeight: 1,
    transition: "transform 0.25s ease",
    flexShrink: 0,
  },
  accordionContent: {
    overflow: "hidden",
    transition: "max-height 0.3s ease, opacity 0.25s ease",
    padding: "0 1.5rem",
  },
  contentList: {
    listStyle: "none",
    margin: 0,
    padding: "0 0 1.5rem",
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.85rem",
  },
  contentItem: {
    display: "flex",
    gap: "0.75rem",
    fontSize: "0.9rem",
    color: "#aaa",
    lineHeight: 1.65,
  },
  bullet: {
    color: "#555",
    flexShrink: 0,
    marginTop: "0.05rem",
  },
  noticeBox: {
    marginTop: "3rem",
    padding: "1.25rem 1.5rem",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "12px",
  },
  noticeText: {
    fontSize: "0.875rem",
    color: "#888",
    lineHeight: 1.7,
    margin: 0,
  },
  footer: {
    marginTop: "4rem",
  },
  footerText: {
    fontSize: "0.95rem",
    color: "#888",
    lineHeight: 1.7,
  },
  link: {
    color: "#e5e5e5",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
  footerLinks: {
    marginTop: "1.25rem",
    display: "flex",
    gap: "1.5rem",
  },
  subLink: {
    fontSize: "0.875rem",
    color: "#666",
    textDecoration: "none",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    paddingBottom: "1px",
    transition: "color 0.2s",
  },
  footerSeparator: {
    height: "1px",
    background: "rgba(255,255,255,0.06)",
    margin: "2.5rem 0 1.5rem",
  },
  copyright: {
    fontSize: "0.8rem",
    color: "#555",
  },
};
